import { Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Params, RouterLink, RouterLinkActive } from '@angular/router';

import { merge, Observable, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, share, switchMap } from 'rxjs/operators';

import { InstalledApplication } from '@rocksoft/mdm-sdk-v2';

import { AsyncPipe } from '@angular/common';
import { AppleDeviceProfileResolverActions, selectProfileItemByType, selectProfileItemsByType } from '@era/stores';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import {
  MasterComponent,
  MasterDetailComponent,
  PanelComponent,
  PanelFooterComponent,
  SkSpinner3Component,
} from '@rocksoft/cdk';
import { isNotNullOrUndefined } from '@rocksoft/shared';
import { ToastService } from '@rocksoft/shared/toast';
import { AppleCommandProgressService } from '../../shared/services/apple-command-progress.service';
import { AppleMdmService } from '../../shared/services/apple-mdm-service';
import { CommandSpinnerComponent } from '../../utilities/command-spinner/command-spinner.component';
import { FileType } from '../../viewmodels/apple/apple-constants.model';
import { RequestType } from '../../viewmodels/apple/commands/apple-command.model';
import { CommandProgress } from '../../viewmodels/apple/signalr/command-context.model';
import { InstallApplicationModalComponent } from './install-application-modal/install-application-modal.component';
import { ManagedApplicationStoreActions } from './stores';

@Component({
  selector: 'ml-mdm-apple-apps',
  templateUrl: './apple-apps.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MasterDetailComponent,
    MasterComponent,
    PanelComponent,
    SkSpinner3Component,
    RouterLinkActive,
    RouterLink,
    PanelFooterComponent,
    FaIconComponent,
    CommandSpinnerComponent,
    AsyncPipe,
  ],
})
export class AppleAppsComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly mdmService = inject(AppleMdmService);
  private readonly commandProgressService = inject(AppleCommandProgressService);
  private readonly store = inject(Store);
  private readonly modalService = inject(NgbModal);
  private readonly toastService = inject(ToastService);

  udid: Observable<string>;

  public fileType: FileType = FileType.Application;

  private _refreshAppsProgress: Subject<CommandProgress> = new Subject<CommandProgress>();
  public refreshAppsProgress: Observable<CommandProgress>;

  private _refreshManagedApplicationsProgress: Subject<CommandProgress> = new Subject<CommandProgress>();
  public refreshManagedApplicationsProgress: Observable<CommandProgress>;

  public apps: Observable<InstalledApplication[]>;

  private _app = new Subject<InstalledApplication>();
  public app: Observable<InstalledApplication | undefined> = this._app.asObservable();

  private readonly subscriptions: Subscription[] = [];

  ngOnInit() {
    const parentRoute = this.route.parent;
    if (!parentRoute) {
      throw new Error('Expected route to have a parent route.');
    }

    this.udid = parentRoute.params.pipe(
      map((p: Params) => p.udid),
      filter((udid) => udid),
      distinctUntilChanged()
    );

    this.subscriptions.push(
      this.udid.subscribe((udid) => {
        this.onUdidChanges(udid);
      })
    );
  }

  private onUdidChanges(udid: string): void {
    this.apps = this.store.select(selectProfileItemsByType('mdm-installed-application'));

    this.app = merge(
      this._app,
      this.apps.pipe(
        filter((a): a is InstalledApplication[] => !!a && a.length > 0),
        map((a: InstalledApplication[]) => a[0])
      )
    ).pipe(
      filter(isNotNullOrUndefined),
      switchMap((p) =>
        this.store.select(
          selectProfileItemByType('mdm-installed-application', p.identifier ?? '') // Identifier can't be required: "This key will always be present on iOS/tvOS but may be missing on macOS."
        )
      )
    );

    this.refreshAppsProgress = merge(
      this._refreshAppsProgress,
      this.commandProgressService.commandsOfRequestType(udid, 'InstalledApplicationList')
    ).pipe(share());

    this.refreshAppsProgress.pipe(filter((progress) => progress.status === 'Acknowledged')).subscribe((progress) => {
      this.store.dispatch(
        AppleDeviceProfileResolverActions.profileRequested({
          udid: progress.udid,
          profileType: 'mdm-installed-application',
        })
      );
    });

    this.refreshManagedApplicationsProgress = merge(
      this._refreshManagedApplicationsProgress,
      this.commandProgressService.commandsOfRequestType(udid, 'ManagedApplicationList')
    ).pipe(share());

    this.refreshManagedApplicationsProgress
      .pipe(filter((progress) => progress.status === 'Acknowledged'))
      .subscribe((progress) => {
        this.store.dispatch(
          ManagedApplicationStoreActions.ManagedApplicationsContainerActions.managedApplicationRequested({
            udid: progress.udid,
          })
        );
      });
  }

  public select(app: InstalledApplication): void {
    this._app.next(app);
  }

  public refresh(udid: string): void {
    this._refreshAppsProgress.next(this.createInitialCommandProgress(udid, 'InstalledApplicationList'));
    this._refreshManagedApplicationsProgress.next(this.createInitialCommandProgress(udid, 'ManagedApplicationList'));

    this.subscriptions.push(this.mdmService.getApps(udid).subscribe());

    this.subscriptions.push(this.mdmService.getManagedApplications(udid).subscribe());
  }

  public showInstallApplicationModal(udid: string) {
    const modal = this.modalService.open(InstallApplicationModalComponent, { scrollable: false, size: 'xl' });
    modal.componentInstance.udid = udid;
  }

  private createInitialCommandProgress(udid: string, requestType: RequestType): CommandProgress {
    return {
      udid,
      commandUUID: '',
      status: 'Sending',
      requestType,
    };
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }
}
